import {
  Archive,
  BadgePercent,
  Boxes,
  FileText,
  LayoutDashboard,
  Library,
  ListTodo,
  type LucideIcon,
  MessageSquare,
  Package,
  Route,
  Settings,
  ShieldCheck,
  ShoppingCart,
  Tags,
  TriangleAlert,
  Umbrella,
} from 'lucide-react';

/**
 * Vienotais navigācijas modelis (sānjosla + komandpalete).
 * Lomas redzamība pārmantota no vecā AppLayout NAV; SALABOTS pārvadātāja caurums:
 * carrier_* redz TIKAI Dashboard, Supply Orders, Documents, Chat (nekad /orders — §7).
 */

export const isPlatform = (r: string) => r.startsWith('platform_') || r === 'auditor';
export const isSupplier = (r: string) => r.startsWith('supplier_');
export const isCustomer = (r: string) => r.startsWith('customer_');
export const isCarrier = (r: string) => r.startsWith('carrier_');

export interface NavItem {
  to: string;
  /** common.json `nav.*` atslēga. */
  key: string;
  icon: LucideIcon;
  show: (role: string) => boolean;
}

export interface NavGroup {
  /** common.json `navGroups.*` atslēga. */
  key: string;
  items: NavItem[];
}

export const NAV_GROUPS: NavGroup[] = [
  {
    key: 'overview',
    items: [{ to: '/', key: 'dashboard', icon: LayoutDashboard, show: () => true }],
  },
  {
    key: 'commerce',
    items: [
      // Piedāvājumi — platforma sastāda, klients apstiprina.
      {
        to: '/offers',
        key: 'offers',
        icon: BadgePercent,
        show: (r) => isPlatform(r) || isCustomer(r),
      },
      // Gala klientu pasūtījumi — piegādātājam UN pārvadātājam liegts (§7).
      {
        to: '/orders',
        key: 'orders',
        icon: ShoppingCart,
        show: (r) => isPlatform(r) || isCustomer(r),
      },
      // Anonimizētie piegādes pasūtījumi — arī pārvadātājs (savi norīkojumi).
      {
        to: '/supply-orders',
        key: 'supplyOrders',
        icon: Boxes,
        show: (r) => isPlatform(r) || isSupplier(r) || isCarrier(r),
      },
      { to: '/pricing', key: 'pricing', icon: Tags, show: (r) => isPlatform(r) || isCustomer(r) },
      { to: '/products', key: 'products', icon: Package, show: (r) => !isCarrier(r) },
    ],
  },
  {
    key: 'logistics',
    items: [
      {
        to: '/documents',
        key: 'documents',
        icon: FileText,
        show: (r) => isPlatform(r) || isSupplier(r) || isCarrier(r),
      },
      {
        to: '/traceability',
        key: 'traceability',
        icon: Route,
        show: (r) => isPlatform(r) || isSupplier(r),
      },
    ],
  },
  {
    key: 'quality',
    items: [
      { to: '/claims', key: 'claims', icon: TriangleAlert, show: (r) => !isCarrier(r) },
      { to: '/compliance', key: 'compliance', icon: ShieldCheck, show: (r) => isPlatform(r) },
      { to: '/insurance', key: 'insurance', icon: Umbrella, show: (r) => isPlatform(r) },
    ],
  },
  {
    key: 'workspace',
    items: [
      {
        to: '/tasks',
        key: 'tasks',
        icon: ListTodo,
        show: (r) => !isCarrier(r) && (!isCustomer(r) || r === 'customer_admin'),
      },
      { to: '/chat', key: 'chat', icon: MessageSquare, show: () => true },
      {
        to: '/archive',
        key: 'archive',
        icon: Archive,
        show: (r) => isPlatform(r) || isSupplier(r),
      },
    ],
  },
  {
    key: 'administration',
    items: [
      {
        to: '/registry',
        key: 'registry',
        icon: Library,
        show: (r) => isPlatform(r) || isSupplier(r),
      },
      {
        to: '/admin',
        key: 'admin',
        icon: Settings,
        show: (r) => r === 'platform_admin' || r === 'platform_super',
      },
    ],
  },
];

/** Redzamās grupas dotajai lomai (tukšās grupas izmet). */
export function visibleGroups(role: string): NavGroup[] {
  return NAV_GROUPS.map((g) => ({ ...g, items: g.items.filter((i) => i.show(role)) })).filter(
    (g) => g.items.length > 0,
  );
}

/** Aktīvais nav ieraksts ceļam: precīza sakritība, tad garākais prefikss (detaļu lapas). */
export function activeNavFor(pathname: string): { group: NavGroup; item: NavItem } | null {
  let best: { group: NavGroup; item: NavItem } | null = null;
  let bestLen = -1;
  for (const group of NAV_GROUPS) {
    for (const item of group.items) {
      if (item.to === pathname) return { group, item };
      if (item.to !== '/' && pathname.startsWith(`${item.to}/`) && item.to.length > bestLen) {
        best = { group, item };
        bestLen = item.to.length;
      }
    }
  }
  return best;
}
